/**
 * The flow's back and forward buttons: one history entry per screen, and a
 * `popstate` handler that copes with entries the run can no longer show.
 *
 * Two things make this more than a `pushState` wrapper.
 *
 * A step can stop being showable after the user has already been on it -- the
 * assessment becomes its result screen once it has produced one -- so an entry
 * sitting in the stack has to be resolved again every time it is landed on,
 * including on the way forward.
 *
 * And an entry that resolves onto the screen already showing has to be
 * travelled *past* rather than swallowed, or the press looks ignored.
 */

import { type OnboardingStep, stepIndex } from "~vue/CalendarFirstOnboarding/types";
import { onMounted, onUnmounted, type Ref } from "vue";

export interface StepHistoryOptions {
    /** The step on screen. This composable is the only thing that moves it. */
    step: Ref<OnboardingStep>;
    /** A requested step reduced to one the run can actually show. */
    resolve: (requested: string | null) => OnboardingStep;
    /** Called once a browser press has changed the screen. */
    onPop?: (step: OnboardingStep) => void;
}

export interface StepHistory {
    /**
     * Point the URL, and the screen, at a step.
     *
     * `replace` is for steps nobody should be able to land on with the back
     * button. `scanning` is the only one: it is a spinner the flow moves itself
     * out of, so a history entry for it would make Back look broken -- press it
     * and you would be bounced straight forward again.
     */
    navigate: (to: OnboardingStep, options?: { replace?: boolean }) => void;
}

export function useStepHistory({ step, resolve, onPop }: StepHistoryOptions): StepHistory {
    /**
     * Write an entry carrying nothing but the step.
     *
     * Deliberately not Inertia's own state. Inertia treats an entry with a
     * `page` key as one of its own and restores that page on a back press,
     * `preserveState: false` -- which remounts this component with the props it
     * first loaded with, dropping the user back at the flow's opening screen.
     * Left without one, Inertia refetches the entry's URL instead, which keeps
     * this component alive and its props in step with the address bar.
     */
    function write(to: OnboardingStep, { replace }: { replace: boolean }): void {
        const url = new URL(globalThis.location.href);
        url.searchParams.set("step", to);
        const state = { step: to };
        if (replace) {
            globalThis.history.replaceState(state, "", url.toString());
        } else {
            globalThis.history.pushState(state, "", url.toString());
        }
    }

    function navigate(to: OnboardingStep, { replace = false }: { replace?: boolean } = {}): void {
        /* Resolved on the way forward too. A Continue pressed on a screen the user
           went back to walks to that screen's successor, which the run may since
           have made unshowable -- re-entering it would open a second assessment
           session, or 409 against a cursor that is already past it. */
        const resolved = resolve(to);
        if (resolved === step.value) {
            return;
        }
        step.value = resolved;
        write(resolved, { replace });
    }

    /** Keep going the way the user pressed, short of walking out of the flow. */
    function travelPast(wentBack: boolean, showing: OnboardingStep): void {
        if (!wentBack) {
            globalThis.history.forward();
            return;
        }
        if (stepIndex(showing) > 0) {
            globalThis.history.back();
        }
    }

    function onPopState(): void {
        const requested = new URLSearchParams(globalThis.location.search).get("step");
        const resolved = resolve(requested);
        /* `popstate` reports no direction, and nothing can be stashed on the entry
           to supply one: Inertia rewrites the state of every entry it visits. The
           flow is strictly ordered and entries only ever move along it, so the
           landed step's position against the one on screen is the direction. */
        const wentBack = stepIndex(requested as OnboardingStep) < stepIndex(step.value);

        if (resolved !== requested) {
            /* The entry the user landed on is not showable, so overwrite it rather
               than leaving the address bar disagreeing with the screen. */
            write(resolved, { replace: true });
        }

        if (resolved === step.value) {
            /* It resolved onto the screen already showing, so honouring it would
               make the press look ignored. */
            if (resolved !== requested) {
                travelPast(wentBack, resolved);
            }
            return;
        }

        step.value = resolved;
        onPop?.(resolved);
    }

    onMounted(() => {
        globalThis.addEventListener("popstate", onPopState);
    });

    onUnmounted(() => {
        globalThis.removeEventListener("popstate", onPopState);
    });

    return { navigate };
}
